import { useState } from 'react';
import { useCreateClinicalEvolution } from '@/hooks';
import { Button } from '@/components/ui/Button';

interface ClinicalEvolutionEditorProps {
  medicalRecordId: string;
  appointmentId: string;
  doctorId: string;
  existingEvolutions?: Array<{
    id: string;
    summary: string;
    conduct: string;
    observations?: string;
    nextAppointment?: string;
    isSigned: boolean;
    signedAt?: string;
  }>;
  onEvolutionAdded?: () => void;
  disabled?: boolean;
}

export function ClinicalEvolutionEditor({
  medicalRecordId,
  appointmentId,
  doctorId,
  existingEvolutions = [],
  onEvolutionAdded,
  disabled = false,
}: ClinicalEvolutionEditorProps) {
  const [showForm, setShowForm] = useState(false);
  const [summary, setSummary] = useState('');
  const [conduct, setConduct] = useState('');
  const [observations, setObservations] = useState('');
  const [nextAppointment, setNextAppointment] = useState('');

  const createEvolution = useCreateClinicalEvolution();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!summary.trim() || !conduct.trim()) return;

    try {
      await createEvolution.mutateAsync({
        medicalRecordId,
        appointmentId,
        doctorId,
        summary: summary.trim(),
        conduct: conduct.trim(),
        observations: observations.trim() || undefined,
        nextAppointment: nextAppointment.trim() || undefined,
      });
      setShowForm(false);
      setSummary('');
      setConduct('');
      setObservations('');
      setNextAppointment('');
      onEvolutionAdded?.();
    } catch (error) {
      console.error('Failed to create clinical evolution:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSummary('');
    setConduct('');
    setObservations('');
    setNextAppointment('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Evoluções Clínicas</h3>
        {!showForm && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            + Nova Evolução
          </Button>
        )}
      </div>

      {existingEvolutions.length > 0 && (
        <div className="space-y-4">
          {existingEvolutions.map((evolution) => (
            <div
              key={evolution.id}
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {evolution.isSigned ? (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      Assinado
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                      Pendente
                    </span>
                  )}
                </div>
                {evolution.isSigned && evolution.signedAt && (
                  <span className="text-xs text-gray-500">
                    {new Date(evolution.signedAt).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Resumo</h4>
                  <p className="mt-1 text-sm text-gray-900">{evolution.summary}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Conduta</h4>
                  <p className="mt-1 text-sm text-gray-900">{evolution.conduct}</p>
                </div>
                {evolution.observations && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700">Observações</h4>
                    <p className="mt-1 text-sm text-gray-600">{evolution.observations}</p>
                  </div>
                )}
                {evolution.nextAppointment && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Próxima consulta: {evolution.nextAppointment}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resumo da Evolução *
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Descreva o resumo da evolução clínica..."
              disabled={disabled || createEvolution.isPending}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conduta *
            </label>
            <textarea
              value={conduct}
              onChange={(e) => setConduct(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Descreva a conduta médica..."
              disabled={disabled || createEvolution.isPending}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações Adicionais
            </label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Observações adicionais..."
              disabled={disabled || createEvolution.isPending}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Próxima Consulta
            </label>
            <input
              type="text"
              value={nextAppointment}
              onChange={(e) => setNextAppointment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Ex: 30 dias, 2 semanas..."
              disabled={disabled || createEvolution.isPending}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={createEvolution.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!summary.trim() || !conduct.trim() || createEvolution.isPending}
            >
              {createEvolution.isPending ? 'Salvando...' : 'Registrar Evolução'}
            </Button>
          </div>
        </form>
      )}

      {!showForm && existingEvolutions.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm">Nenhuma evolução clínica registrada.</p>
          <p className="text-xs mt-1">Clique em "Nova Evolução" para começar.</p>
        </div>
      )}
    </div>
  );
}
